import { Box, TextField, type TextFieldProps } from '@chronosphereio/chrono-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FieldSuggestionProps } from '@/plugin-system';

export interface FieldWithSuggestionProps extends Omit<TextFieldProps, 'label' | 'onChange' | 'sx' | 'value'> {
  value: string;
  onChange: (value: string) => void;
  label: string;
  suggestion?: FieldSuggestionProps;
}

/**
 * Renders a text field with AI suggestion overlay support (auto-fill and ghost).
 * Used by PanelEditorForm for panel name/description when suggestions are enabled.
 */
export function FieldWithSuggestion({
  value,
  onChange,
  label,
  suggestion,
  InputLabelProps,
  InputProps,
  ...textFieldProps
}: FieldWithSuggestionProps) {
  const [focused, setFocused] = useState(false);
  const [userModified, setUserModified] = useState(false);
  const prevSuggestionRef = useRef<string | null>(null);

  const isEmpty = !value.trim();
  const suggestionText = suggestion?.suggestionText ?? null;
  const hasAutoFill = Boolean(suggestion?.autoFillOverlay);

  useEffect(() => {
    if (!suggestionText && userModified) {
      setUserModified(false);
    }
  }, [suggestionText, userModified]);

  const isAutoFilled =
    hasAutoFill &&
    !userModified &&
    !!suggestionText &&
    value.trim().toLowerCase() === suggestionText.trim().toLowerCase();

  const valueRef = useRef(value);
  valueRef.current = value;
  const userModifiedRef = useRef(userModified);
  userModifiedRef.current = userModified;

  useEffect(() => {
    if (!hasAutoFill || userModifiedRef.current || !suggestionText) return;
    const currentValue = valueRef.current;
    const prevSuggestion = prevSuggestionRef.current;
    const isFieldEmpty = !currentValue.trim();
    const matchesPrevSuggestion =
      !!prevSuggestion && currentValue.trim().toLowerCase() === prevSuggestion.trim().toLowerCase();
    if (isFieldEmpty || matchesPrevSuggestion) {
      onChange(suggestionText);
    }
    prevSuggestionRef.current = suggestionText;
  }, [suggestionText, hasAutoFill, onChange]);

  const showAutoFillOverlay = isAutoFilled && !focused;
  const showGhostOverlay = !isAutoFilled && isEmpty && focused && Boolean(suggestion?.overlay);
  const hasOverlay = showAutoFillOverlay || showGhostOverlay;

  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => setFocused(false), []);
  const handleChange = useCallback(
    (newValue: string) => {
      setUserModified(true);
      onChange(newValue);
    },
    [onChange]
  );
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (suggestion && isEmpty) {
        suggestion.onKeyDown(e, value);
      }
    },
    [suggestion, value, isEmpty]
  );

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <TextField
          {...textFieldProps}
          fullWidth
          label={label}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          InputLabelProps={{ ...InputLabelProps, ...(hasOverlay ? { shrink: true } : undefined) }}
          InputProps={{
            ...InputProps,
            onFocus: suggestion ? handleFocus : undefined,
            onBlur: suggestion ? handleBlur : undefined,
            onKeyDown: handleKeyDown,
            sx: showAutoFillOverlay ? { '& input': { color: 'transparent', caretColor: 'transparent' } } : undefined,
          }}
        />
        {isAutoFilled && suggestion?.autoFillOverlay ? (
          <div style={{ visibility: focused ? 'hidden' : 'visible' }}>{suggestion.autoFillOverlay}</div>
        ) : null}
        {showGhostOverlay ? suggestion?.overlay : null}
      </Box>
      {suggestion?.hint ? suggestion.hint({ focused, isEmpty, isAutoFilled }) : null}
    </>
  );
}
